import {Injectable} from '@angular/core';
import {DocumentDraft, OutlineMode} from '../interfaces/document-draft.dto';
import {environment} from '../../../environments/environment';
import {ExtendMetadataDto} from '../interfaces/extend-metadata.dto';
import {map, Observable, retry, throwError, timer} from 'rxjs';
import {HttpClient, HttpErrorResponse, HttpEvent} from '@angular/common/http';
import {ChatMessage} from '../interfaces/thread-messages.dto';
import {ExtendMetaResponse} from '../interfaces/extend-meta.response';
import {Job} from '../interfaces/jobs.dto';
import {AIGenerationOptions} from '../../shared/components/ai-generation-bubble/ai-generation-bubble.component';
import {AI_RETRY_ATTEMPTS, AI_RETRY_DELAY_MS, isTransientAiFailure} from '@maporium-workspace/shared';

export type PreflightResponse =
  | { status: 'fits'; extractedKey: string; metadata: { title: string; author: string; pages: number } }
  | { status: 'needs_confirmation'; extractedKey: string; partialPercent: number; metadata: { title: string; author: string; pages: number } }
  | { status: 'too_large'; oversizePercent: number }
  | { status: 'no_text' };

@Injectable({ providedIn: 'root' })
export class OpenaiResource {
  private readonly baseUrl = environment.apiUrl;

  constructor(private readonly http: HttpClient) {
  }

  extendMetadata(dto: ExtendMetadataDto, mapId: string, options?: AIGenerationOptions): Observable<ExtendMetaResponse | null> {
    return this.http.post<ExtendMetadataDto>(this.baseUrl + '/ai/extend-metadata', {
      dto,
      extendMetaOnly: false,
      mapId,
      options
    })
      .pipe(
        // MAP-1587: this call is synchronous, so a transient 5xx reaches the
        // user as a hard failure that a second click then resolves. Retry it
        // once, quietly, and only for statuses that mean "try again".
        retry({
          count: AI_RETRY_ATTEMPTS,
          delay: (error: HttpErrorResponse) =>
            isTransientAiFailure(error?.status)
              ? timer(AI_RETRY_DELAY_MS)
              : throwError(() => error)
        }),
        map((response: any) => {
          // Backend now returns clean JSON directly, no parsing needed
          return response as ExtendMetaResponse;
        })
      );
  }

  generateAnswer(question: string, mapId: string, screenshot?: Blob, mapCenter?: {
    x: number;
    y: number
  }, webSearch?: boolean): Observable<Job> {
    const formData = new FormData();
    formData.append('question', question);
    formData.append('mapId', mapId);
    if (screenshot) {
      formData.append('screenshot', new File([screenshot], 'map.png', {type: 'image/png'}));
    }
    if (mapCenter) {
      formData.append('mapCenterX', String(mapCenter.x));
      formData.append('mapCenterY', String(mapCenter.y));
    }
    if (webSearch) {
      formData.append('webSearch', 'true');
    }
    return this.http.post<Job>(this.baseUrl + '/ai/map-assistant', formData);
  }


  getMessages(threadId: string, limit = 20, after: string): Observable<any> {
    return this.http.get(this.baseUrl + `/ai/message/${threadId}?limit=${limit}&after=${after}`);
  }

  getMessagesByMap(mapId: string, limit = 20, after?: string): Observable<ChatMessage[]> {
    return this.http.get<ChatMessage[]>(this.baseUrl + `/ai/message/map/${mapId}?limit=${limit}&after=${after}`);
  }

  describeView(savedViewId: string, mapId: string): Observable<Job> {
    return this.http.post<Job>(this.baseUrl + '/ai/describe-view', {
      savedViewId,
      mapId
    });
  }

  describeNode(nodeId: string, mapId: string): Observable<Job> {
    return this.http.post<Job>(this.baseUrl + '/ai/describe-node', {
      nodeId,
      mapId
    });
  }

  getJobStatus(jobId: string): Observable<Job> {
    return this.http.get<Job>(this.baseUrl + `/jobs/find/${jobId}`);
  }

  validateOpenAIKey(apiKey: string): Observable<{ valid: boolean; models: any[]; error?: string }> {
    return this.http.post<{ valid: boolean; models: any[]; error?: string }>(
      this.baseUrl + '/ai/validate-key',
      { apiKey }
    );
  }

  describeLink(id: string, mapId: string) {
    return this.http.post<Job>(this.baseUrl + '/ai/describe-link', {
      linkId: id,
      mapId
    });
  }

  getDocumentUploadUrl(filename: string, contentType?: string): Observable<{ uploadUrl: string; key: string; maxBytes: number }> {
    return this.http.post<{ uploadUrl: string; key: string; maxBytes: number }>(
      this.baseUrl + '/ai/document-upload-url',
      {filename, contentType},
    );
  }

  // Direct PUT to the presigned S3 URL — bypasses our API entirely so large
  // PDFs aren't subject to Heroku's 30s router timeout. `withCredentials`
  // must be false; sending cookies/auth to S3 breaks the signature.
  uploadToPresignedUrlWithProgress(uploadUrl: string, file: File): Observable<HttpEvent<unknown>> {
    return this.http.put(uploadUrl, file, {
      headers: {'Content-Type': file.type || 'application/octet-stream'},
      withCredentials: false,
      reportProgress: true,
      observe: 'events',
    });
  }

  uploadToPresignedUrl(uploadUrl: string, file: File): Observable<void> {
    return this.http.put<void>(uploadUrl, file, {
      headers: {'Content-Type': file.type || 'application/octet-stream'},
      withCredentials: false,
    });
  }

  convertDocumentToMap(s3Key: string, originalname: string, additionalInstructions?: string, extractedKey?: string): Observable<Job> {
    return this.http.post<Job>(this.baseUrl + '/ai/convert-document', {
      s3Key,
      originalname,
      additionalInstructions,
      extractedKey,
    });
  }

  createDocumentDraft(): Observable<{draftId: string}> {
    return this.http.post<{draftId: string}>(this.baseUrl + '/ai/document-drafts', {});
  }

  addDraftDocument(id: string, originalname: string): Observable<{documentId: string}> {
    return this.http.post<{documentId: string}>(
      this.baseUrl + `/ai/document-drafts/${encodeURIComponent(id)}/documents`, {originalname});
  }

  startDraftIngest(id: string, documentId: string, s3Key: string): Observable<{jobId: string}> {
    return this.http.post<{jobId: string}>(
      this.baseUrl + `/ai/document-drafts/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}/ingest`,
      {s3Key});
  }

  removeDraftDocument(id: string, documentId: string): Observable<void> {
    return this.http.delete<void>(
      this.baseUrl + `/ai/document-drafts/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}`);
  }

  getDocumentDraft(id: string): Observable<DocumentDraft> {
    return this.http.get<DocumentDraft>(this.baseUrl + `/ai/document-drafts/${encodeURIComponent(id)}`);
  }

  sendDraftTurn(
    id: string,
    message: string,
    options?: {webSearch?: boolean; includeResources?: boolean; includeImages?: boolean}
  ): Observable<{jobId: string}> {
    return this.http.post<{jobId: string}>(
      this.baseUrl + `/ai/document-drafts/${encodeURIComponent(id)}/turns`, {message, ...options});
  }

  commitDocumentDraft(id: string, turnId?: string): Observable<{mapId: string}> {
    return this.http.post<{mapId: string}>(this.baseUrl + `/ai/document-drafts/${encodeURIComponent(id)}/commit`, {turnId});
  }

  reindexDocumentDraft(id: string, mode: OutlineMode): Observable<{draftId: string}> {
    return this.http.post<{draftId: string}>(
      this.baseUrl + `/ai/document-drafts/${encodeURIComponent(id)}/reindex`, {mode});
  }

  discardDocumentDraft(id: string): Observable<void> {
    return this.http.delete<void>(this.baseUrl + `/ai/document-drafts/${encodeURIComponent(id)}`);
  }

  preflightDocument(s3Key: string, originalname: string): Observable<PreflightResponse> {
    return this.http.post<PreflightResponse>(this.baseUrl + '/ai/convert-document/preflight', {
      s3Key,
      originalname,
    });
  }

  generateSmapFromPrompt(prompt: string, webSearch?: boolean, includeResources?: boolean, includeImages?: boolean): Observable<Job> {
    return this.http.post<Job>(this.baseUrl + '/ai/generate-from-prompt', {prompt, webSearch, includeResources, includeImages});
  }

  findImage(name: string, description: string, target: 'node' | 'smap'): Observable<{ imageUrl: string | null }> {
    return this.http.post<{ imageUrl: string | null }>(`${this.baseUrl}/ai/find-image`, {name, description, target});
  }

  analyzeBottlenecks(mapId: string): Observable<Job> {
    return this.http.post<Job>(`${this.baseUrl}/ai/analyze-bottlenecks`, {mapId});
  }

  generateModels(mapId: string): Observable<Job> {
    return this.http.post<Job>(`${this.baseUrl}/ai/generate-models`, {mapId});
  }

  optimizeMap(mapId: string): Observable<Job> {
    return this.http.post<Job>(`${this.baseUrl}/ai/optimize-map`, {mapId});
  }

  generateProposal(mapId: string): Observable<Job> {
    return this.http.post<Job>(`${this.baseUrl}/ai/generate-proposal`, {mapId});
  }

  getProposalsForMap(mapId: string): Observable<{id: string; publicSlug: string; createdAt: string}[]> {
    return this.http.get<{id: string; publicSlug: string; createdAt: string}[]>(`${this.baseUrl}/ai/proposals/${mapId}`);
  }
}
